import { registrarError, traducirErrorSupabase } from '../../domain/errores';
import { aNumero, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';
import { ambitoSesion } from './AmbitoSesion';
import { filtroDemo } from './FiltroDemo';

/** Elemento de un desplegable: lo mínimo para elegir algo. */
export interface Opcion {
  id: number;
  nombre: string;
}

/** Área con el cliente al que pertenece, para poder acotar desplegables. */
export interface OpcionArea extends Opcion {
  clienteId: number | null;
  /** Dónde está, ya compuesto: «Cliente · Área». */
  ubicacion?: string;
}

/** Nodo con el área de la que cuelga, para las cascadas de los formularios. */
export interface OpcionNodo extends Opcion {
  areaId: number | null;
  /** Dónde está, ya compuesto: «Cliente · Área». */
  ubicacion: string;
}

/** Normaliza filas que unas vistas nombran `id/nombre` y otras `x_id/x_nombre`. */
function aOpciones(filas: readonly Record<string, unknown>[], prefijo: string): Opcion[] {
  return filas
    .map((f) => ({
      id: aNumero(f[`${prefijo}_id`] ?? f.id),
      nombre: aTexto(f[`${prefijo}_nombre`] ?? f.nombre),
    }))
    .filter((o): o is Opcion => o.id !== null && o.nombre !== null)
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

/**
 * Catálogos para los desplegables de filtro.
 *
 * Devuelve sólo identificador y nombre: es lo único que necesitan los filtros,
 * y pedir la fila completa para llenar un desplegable era una de las consultas
 * más pesadas e innecesarias de la versión anterior.
 *
 * El alcance lo aplican las políticas del servidor: un usuario sólo recibe los
 * clientes y áreas que le corresponden, sin que la pantalla tenga que filtrar.
 */
export class RepositorioCatalogos {
  async clientes(): Promise<Opcion[]> {
    const { data, error } = await filtroDemo
      .aplicar(
        'clientes',
        ambitoSesion.aplicar(supabase.from('clientes').select('id, nombre'), 'id'),
      )
      .order('nombre', { ascending: true });
    if (error) throw traducirErrorSupabase(error);
    return aOpciones((data ?? []) as Record<string, unknown>[], 'cliente');
  }

  async areasDeCliente(clienteId: number | null): Promise<Opcion[]> {
    if (!clienteId) return [];
    // Un cliente fuera del ámbito no tiene áreas que ofrecer.
    if (!ambitoSesion.permite(clienteId)) return [];

    const { data, error } = await supabase
      .from('areas')
      .select('id, nombre')
      .eq('cliente_id', clienteId);

    if (error) throw traducirErrorSupabase(error);
    return aOpciones((data ?? []) as Record<string, unknown>[], 'area');
  }

  async nodosDeArea(areaId: number | null): Promise<Opcion[]> {
    if (!areaId) return [];

    const { data, error } = await supabase
      .from('vw_nodos_info')
      .select('nodo_id, nodo_nombre')
      .eq('area_id', areaId);

    if (error) throw traducirErrorSupabase(error);
    return aOpciones((data ?? []) as Record<string, unknown>[], 'nodo');
  }

  /**
   * Todas las áreas visibles, sin acotar por cliente.
   * Los filtros en cascada nunca lo necesitan, pero los formularios de alta sí:
   * al crear un nodo hay que poder elegir cualquier área.
   */
  async todasLasAreas(): Promise<Opcion[]> {
    const { data, error } = await filtroDemo.aplicar(
      'areas',
      ambitoSesion.aplicar(supabase.from('areas').select('id, nombre')),
    );
    if (error) throw traducirErrorSupabase(error);
    return aOpciones((data ?? []) as Record<string, unknown>[], 'area');
  }

  /**
   * Todas las áreas del ámbito, cada una con su cliente.
   *
   * `todasLasAreas` no lo trae, y hace falta para que al elegir un cliente en
   * un formulario el desplegable de áreas muestre sólo las suyas sin tener que
   * ir al servidor en cada cambio.
   */
  async areasConCliente(): Promise<OpcionArea[]> {
    const { data, error } = await filtroDemo.aplicar(
      'areas',
      ambitoSesion.aplicar(supabase.from('areas').select('id, nombre, cliente_id')),
    );
    if (error) throw traducirErrorSupabase(error);

    return ((data ?? []) as Record<string, unknown>[])
      .map((fila) => ({
        id: aNumero(fila.id) ?? 0,
        nombre: aTexto(fila.nombre) ?? 'Sin nombre',
        clienteId: aNumero(fila.cliente_id),
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }

  /**
   * Nodos con su cliente y su área.
   *
   * `todosLosNodos` sólo devuelve el nombre, y hay instalaciones con varios
   * nodos llamados igual —«Actuador», «Protecciones eléctricas»— en clientes
   * distintos. Sin la ubicación no hay forma de saber cuál se está eligiendo.
   */
  async nodosConUbicacion(): Promise<OpcionArea[]> {
    const { data, error } = await filtroDemo.aplicar(
      'vw_nodos_info',
      ambitoSesion.aplicar(
        supabase.from('vw_nodos_info').select('nodo_id, nodo_nombre, cliente_nombre, area_nombre'),
      ),
    );

    if (error) throw traducirErrorSupabase(error);

    return ((data ?? []) as Record<string, unknown>[])
      .map((fila) => ({
        id: aNumero(fila.nodo_id) ?? 0,
        nombre: aTexto(fila.nodo_nombre) ?? 'Sin nombre',
        // Se reutiliza `clienteId` de `OpcionArea` para no crear otro tipo con
        // la misma forma; lo que importa aquí es el texto de la ubicación.
        clienteId: null,
        ubicacion:
          [aTexto(fila.cliente_nombre), aTexto(fila.area_nombre)].filter(Boolean).join(' · ') ||
          '—',
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }

  /**
   * Nodos con el identificador de su área.
   *
   * `nodosDeArea` va al servidor con cada cambio de área, que en un formulario
   * significa una consulta por cada clic. Aquí se traen todos una vez y el
   * desplegable se acota en memoria, que es instantáneo. El cliente no se pide
   * a esta vista: se deduce del área, que es de quien cuelga.
   */
  async nodosConArea(): Promise<OpcionNodo[]> {
    const { data, error } = await filtroDemo.aplicar(
      'vw_nodos_info',
      ambitoSesion.aplicar(
        supabase
          .from('vw_nodos_info')
          .select('nodo_id, nodo_nombre, area_id, cliente_nombre, area_nombre'),
      ),
    );

    if (error) throw traducirErrorSupabase(error);

    return ((data ?? []) as Record<string, unknown>[])
      .map((fila) => ({
        id: aNumero(fila.nodo_id) ?? 0,
        nombre: aTexto(fila.nodo_nombre) ?? 'Sin nombre',
        areaId: aNumero(fila.area_id),
        ubicacion:
          [aTexto(fila.cliente_nombre), aTexto(fila.area_nombre)].filter(Boolean).join(' · ') ||
          '—',
      }))
      .filter((nodo) => nodo.id !== 0)
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }

  async todosLosNodos(): Promise<Opcion[]> {
    const { data, error } = await filtroDemo.aplicar(
      'vw_nodos_info',
      ambitoSesion.aplicar(supabase.from('vw_nodos_info').select('nodo_id, nodo_nombre')),
    );
    if (error) throw traducirErrorSupabase(error);
    return aOpciones((data ?? []) as Record<string, unknown>[], 'nodo');
  }
  /**
   * Logotipo propio del cliente, si tiene uno subido.
   *
   * No lanza si falla: quedarse sin logotipo es un detalle estético y no
   * debe impedir que el armazón de la aplicación se dibuje.
   */
  async logoDeCliente(clienteId: number | null): Promise<string | null> {
    if (clienteId === null) return null;

    const { data, error } = await supabase
      .from('clientes')
      .select('logo_url')
      .eq('id', clienteId)
      .maybeSingle();

    if (error) {
      registrarError('catalogos.logoCliente', error);
      return null;
    }

    return aTexto((data as Record<string, unknown> | null)?.logo_url) ?? null;
  }
}

export const repositorioCatalogos = new RepositorioCatalogos();
